/**
 * @file lastOneLeft.ts
 * @brief "Last one left" speaker suggestion on Resolve Speakers (W30, B37).
 *
 * @details
 * The backend adds `suggestedUser` (suggestedBy "last_one_left") to one
 * `resolved_speakers` row when the screen opens, for the owner only. This is
 * the same rule run on the form as the owner names people, so the suggestion
 * appears when they name the second-last voice by hand and goes away as soon
 * as it stops holding (docs/product/speaker-naming.md, "Live update").
 *
 * Rule (mirrors BACKEND src/features/boardroom/lastOneLeft.mjs): exactly one
 * unnamed row (not flagged `unknownVoice`) and exactly one person on this
 * meeting's own roster not used by a named row. Names come only from
 * `meeting.participants`, never the company list. Never applied: the owner
 * accepts it like a pick from the list. Pure; £0.
 *
 * @author DES
 * @date 28-10-2026
 */

export const LAST_ONE_LEFT = "last_one_left";

/** A row on the form: what it is named as now. */
export type LastOneLeftRow = {
  speakerId: string;
  name: string;
  email: string;
  userId?: string | null;
  unknownVoice?: boolean;
};

/** A roster entry (`meeting.participants[]`), loosely typed as the API sends it. */
export type LastOneLeftRosterEntry = {
  name?: string | null;
  email?: string | null;
  userId?: string | null;
  user_id?: string | null;
  isExternal?: boolean | null;
  isRegisteredUser?: boolean | null;
  profileImageUrl?: string | null;
  profile_image_url?: string | null;
};

export type SuggestedPerson = {
  name: string;
  email: string;
  userId: string | null;
  profileImageUrl: string | null;
  isExternal: boolean;
  isRegisteredUser: boolean;
  suggestedBy: typeof LAST_ONE_LEFT;
};

export type LastOneLeft = { speakerId: string; person: SuggestedPerson };

const RAW_SPEAKER_RE = /^(speaker[_\s-]?\d+|unknown(\s+speaker)?)$/i;
const TRACK_LABEL_RE = /^speaker\s+track\s*#?\s*\d+$/i;
const clean = (v: unknown, max = 120) =>
  String(v ?? "").trim().replace(/\s+/g, " ").slice(0, max);
const lower = (v: unknown) => clean(v, 320).toLowerCase();
const realName = (v: unknown) => {
  const n = clean(v);
  return n && !RAW_SPEAKER_RE.test(n) && !TRACK_LABEL_RE.test(n) ? n : "";
};

function rowPerson(r: LastOneLeftRow) {
  const name = realName(r.name);
  const email = lower(r.email);
  const userId = r.userId || null;
  if (!name && !email && !userId) return null;
  return { name, email, userId };
}

/** Keys for one roster person: user id, email, then name for an email-less guest. */
function personKeys(p: LastOneLeftRosterEntry) {
  const keys: string[] = [];
  const id = p.userId ?? p.user_id;
  if (id) keys.push(`u:${id}`);
  const email = lower(p.email);
  if (email) keys.push(`e:${email}`);
  const name = realName(p.name).toLowerCase();
  if (name && !email) keys.push(`n:${name}`);
  return keys;
}

/** The single suggestion for these rows and this roster, or null. */
export function findLastOneLeft(
  rows: readonly LastOneLeftRow[] | null | undefined,
  roster: readonly LastOneLeftRosterEntry[] | null | undefined,
): LastOneLeft | null {
  const tracks = (rows ?? []).filter((r) => r && r.speakerId);
  const unnamed = tracks.filter((r) => !rowPerson(r));
  if (unnamed.length !== 1 || unnamed[0]!.unknownVoice) return null;

  const used = new Set<string>();
  for (const r of tracks) {
    const who = rowPerson(r);
    if (!who) continue;
    if (who.userId) used.add(`u:${who.userId}`);
    if (who.email) used.add(`e:${who.email}`);
    if (who.name) used.add(`n:${who.name.toLowerCase()}`);
  }

  const seen = new Set<string>();
  const free: LastOneLeftRosterEntry[] = [];
  for (const p of roster ?? []) {
    if (!p) continue;
    const name = realName(p.name);
    if (!name) continue;
    const keys = personKeys(p);
    if (keys.some((k) => seen.has(k))) continue;
    keys.forEach((k) => seen.add(k));
    const isUsed = keys.some((k) => used.has(k)) || used.has(`n:${name.toLowerCase()}`);
    if (!isUsed) free.push(p);
  }
  if (free.length !== 1) return null;

  const p = free[0]!;
  const userId = p.userId || p.user_id || null;
  return {
    speakerId: unnamed[0]!.speakerId,
    person: {
      name: realName(p.name),
      email: lower(p.email),
      userId,
      profileImageUrl: p.profileImageUrl || p.profile_image_url || null,
      isExternal: p.isExternal === true,
      isRegisteredUser: !!userId || p.isRegisteredUser === true,
      suggestedBy: LAST_ONE_LEFT,
    },
  };
}

/** The server's suggestion on one `resolved_speakers` row, when it is a valid one. */
export function serverSuggestion(row: { suggestedUser?: unknown } | null | undefined): SuggestedPerson | null {
  const s = row?.suggestedUser as Partial<SuggestedPerson> | null | undefined;
  if (!s || s.suggestedBy !== LAST_ONE_LEFT) return null;
  const name = realName(s.name);
  if (!name) return null;
  return {
    name,
    email: lower(s.email),
    userId: s.userId || null,
    profileImageUrl: s.profileImageUrl || null,
    isExternal: s.isExternal === true,
    isRegisteredUser: s.isRegisteredUser === true,
    suggestedBy: LAST_ONE_LEFT,
  };
}

/**
 * What Resolve Speakers shows. Owner only (a company admin or shared viewer
 * never gets one). While the form is as it opened, the server's value wins;
 * once the owner changes a name, the rule runs on the form. A row that has a
 * name never shows one.
 */
export function pickSuggestion({
  isOwner,
  rows,
  roster,
  untouched,
  fromServer,
}: {
  isOwner: boolean;
  rows: readonly LastOneLeftRow[];
  roster: readonly LastOneLeftRosterEntry[] | null | undefined;
  untouched: boolean;
  fromServer: LastOneLeft | null;
}): LastOneLeft | null {
  if (!isOwner) return null;
  const hit =
    untouched && fromServer ? fromServer : findLastOneLeft(rows, roster);
  if (!hit) return null;
  const row = rows.find((r) => r.speakerId === hit.speakerId);
  return row && !rowPerson(row) ? hit : null;
}
